/**
 * Frontend observability: Sentry (JS errors + session replay) and CloudWatch
 * RUM (web vitals, page views, HTTP, X-Ray trace linkage, custom events).
 * Call `initMonitoring()` once at application startup before rendering.
 *
 * Both SDKs are dynamically imported so they only load when configured —
 * the initial bundle stays free of monitoring code for local dev and for
 * environments without a DSN / app monitor. `aws-rum-web` is therefore a lazy
 * chunk, never part of the entry.
 *
 * Trace recording/export is disabled. RUM still collects errors, web vitals,
 * failed HTTP requests and page views. The API clients own `traceparent`,
 * carrying an unsampled ID for log correlation, plus `x-request-id` for the
 * error toast and backend access log. Exactly one writer must own the header:
 * XHR combines duplicate values into an invalid comma-separated traceparent.
 */
import type { AwsRum, AwsRumConfig } from 'aws-rum-web';
import { deriveDeployment } from '@/version';
import { useAuthStore } from '@/stores/auth.store';
import { useTenantStore } from '@/stores/tenant.store';
import { getActiveTenantId } from '@/lib/activeTenant';
import { isApiError } from '@/lib/api/errors';
import {
  isTraceparentOwnedExternally,
  setTraceparentOwnedExternally,
} from '@/lib/telemetry/trace-context';

// Re-enable only together with the backend/collector and RUM AppMonitor config.
const RUM_TRACING_ENABLED = false;

// Module-level references populated after initMonitoring() resolves.
// captureQueryError / trackAction / recordRoutePageView check these before
// calling through, so every public function here is a safe no-op until then.
let _sentry: typeof import('@sentry/react') | null = null;
let _rum: AwsRum | null = null;

/** Last page recorded (or waiting to be recorded once RUM initializes). */
let _pendingPage: { pageId: string; routeId: string } | null = null;

/** Last session attributes pushed to RUM; avoids re-sending identical values. */
let _lastSessionAttributes: string | null = null;

const DEFAULT_RUM_REGION = 'us-east-1';

export interface RumRuntimeConfig {
  appMonitorId: string;
  identityPoolId: string;
  guestRoleArn: string | undefined;
  region: string;
  /** Share of sessions RUM records (0..1). */
  sessionSampleRate: number;
}

/** Production keeps half of the sessions; every other deployment keeps all. */
export const DEFAULT_RUM_SESSION_SAMPLE_RATE: Readonly<Record<string, number>> = {
  production: 0.5,
};

/**
 * `VITE_RUM_SESSION_SAMPLE_RATE` (served by /config.json) wins when it is a
 * number in 0..1; otherwise the deployment default applies. Every RUM event
 * is billed, so the rate is a cost knob that must be adjustable per stage
 * without a rebuild.
 */
export function resolveRumSessionSampleRate(
  raw: string | undefined,
  deployment: string = deriveDeployment(import.meta.env.MODE),
): number {
  const parsed = Number(raw?.trim());
  if (raw?.trim() && Number.isFinite(parsed) && parsed >= 0 && parsed <= 1) return parsed;
  return DEFAULT_RUM_SESSION_SAMPLE_RATE[deployment] ?? 1;
}

/**
 * Read the RUM runtime config. Null unless BOTH the app monitor id and the
 * identity pool id are present — the guest role ARN and region only refine a
 * config that is already enabled.
 */
export function readRumConfig(): RumRuntimeConfig | null {
  const appMonitorId = import.meta.env.VITE_RUM_APP_MONITOR_ID?.trim();
  const identityPoolId = import.meta.env.VITE_RUM_IDENTITY_POOL_ID?.trim();
  if (!appMonitorId || !identityPoolId) return null;
  return {
    appMonitorId,
    identityPoolId,
    guestRoleArn: import.meta.env.VITE_RUM_GUEST_ROLE_ARN?.trim() || undefined,
    region: import.meta.env.VITE_RUM_REGION?.trim() || DEFAULT_RUM_REGION,
    sessionSampleRate: resolveRumSessionSampleRate(import.meta.env.VITE_RUM_SESSION_SAMPLE_RATE),
  };
}

/**
 * Sentry options, kept pure so the contract is testable. One build artifact is
 * promoted across stages, so `environment` comes from the hostname (like the
 * version banner), never from Vite's mode, which reads `production` on staging
 * too. The release is the build sha the RUM client already reports. Tracing is
 * off alongside RUM tracing; logs retain correlation IDs without exporting spans.
 */
export function sentryInitOptions(dsn: string) {
  return {
    dsn,
    // Must equal the name `sentryVitePlugin` files the source maps under
    // (`release: { name: appVersion.sha }` in vite.config.ts), so it is the
    // build sha and never `VITE_APP_VERSION`, which RUM's version string may use.
    release: import.meta.env.VITE_APP_VERSION_SHA || 'unknown',
    environment: deriveDeployment(import.meta.env.MODE),
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
  };
}

function applicationVersion(): string {
  return import.meta.env.VITE_APP_VERSION || import.meta.env.VITE_APP_VERSION_SHA || 'unknown';
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * URL matchers for "our API": the bases the axios clients are built on. RUM
 * only traces, records and decorates requests to these — third-party calls
 * (Google Maps, Stripe, Telnyx…) neither gain a `traceparent` preflight header
 * nor leak their query strings into RUM events. A relative base (`/api` on
 * staging/production, where CloudFront fronts the ALB) is matched both bare
 * and against the page origin, since axios passes relative URLs to XHR as-is.
 */
export function apiUrlMatchers(
  bases: ReadonlyArray<string | undefined> = [
    import.meta.env.VITE_OPERATIONS_URL,
    import.meta.env.VITE_AI_OPS_URL,
    import.meta.env.VITE_API_BASE_URL as string | undefined,
  ],
  origin: string = typeof window === 'undefined' ? '' : window.location.origin,
): RegExp[] {
  const matchers: RegExp[] = [];
  const seen = new Set<string>();
  for (const raw of bases) {
    const base = raw?.trim().replace(/\/+$/, '');
    if (!base || seen.has(base)) continue;
    seen.add(base);
    if (/^https?:\/\//i.test(base)) {
      matchers.push(new RegExp(`^${escapeRegExp(base)}(?:/|$)`, 'i'));
    } else {
      const path = base.startsWith('/') ? base : `/${base}`;
      matchers.push(new RegExp(`^${escapeRegExp(path)}(?:/|$)`, 'i'));
      if (origin) {
        matchers.push(new RegExp(`^${escapeRegExp(origin + path)}(?:/|$)`, 'i'));
      }
    }
  }
  // Nothing configured at all (bare relative calls): keep the /api convention.
  if (matchers.length === 0) {
    matchers.push(/^\/api(?:\/|$)/i);
    if (origin) matchers.push(new RegExp(`^${escapeRegExp(origin)}/api(?:/|$)`, 'i'));
  }
  return matchers;
}

/**
 * Browser errors RUM must not count as ours. Both come from outside our code:
 *
 * 1. `@telnyx/webrtc` reads `this.instance.signalingState` / `.addTrack` after
 *    its own `close()` set `this.instance = null` (its pc event handlers and
 *    `_createAnswer` keep running after hang-up; our `releasePeer` triggers
 *    that close). Six of the seven JS errors that put the production monitor
 *    in CRITICAL on 2026-09-15 were this, from one call ending while the user
 *    browsed elsewhere in the dashboard. Matched on the message AND on the
 *    frame that threw: `filename` (window.onerror) or the first stack frame
 *    (unhandled rejection) must be the SDK's own chunk, `vendor-telnyx-*.js`,
 *    pinned in vite.config.ts. The same message thrown by our code — even
 *    from a callback the SDK invoked — still counts. Chrome, Safari and
 *    Firefox word the null read differently; all three forms name the field.
 * 2. `ResizeObserver loop …` — the browser noting a layout pass ran long;
 *    nothing failed. Sentry drops both wordings by default
 *    (`DEFAULT_IGNORE_ERRORS`); RUM ships no default list.
 *
 * Never throws: it runs inside the global error handler, where a throw would
 * replace the original error with one attributed to this file. Receives what
 * the `errors` plugin hands to `ignore` — an `ErrorEvent` or a
 * `PromiseRejectionEvent` — and reads message and origin from whichever it is.
 */
const TELNYX_CHUNK = /\/vendor-telnyx-[^/\s]*\.js/;
const TELNYX_TORN_DOWN_PEER = [
  // Chrome / Edge (V8)
  /^(?:Uncaught )?TypeError: Cannot read properties of null \(reading '(?:signalingState|addTrack)'\)$/,
  /^Cannot read properties of null \(reading '(?:signalingState|addTrack)'\)$/,
  // Safari (JavaScriptCore)
  /^(?:TypeError: )?null is not an object \(evaluating '[\w.]*\.(?:signalingState|addTrack)'\)$/,
  // Firefox (SpiderMonkey)
  /^(?:TypeError: )?can't access property "(?:signalingState|addTrack)", [\w.]+ is null$/,
];
const RESIZE_OBSERVER_LOOP = [
  /^ResizeObserver loop completed with undelivered notifications\.?$/,
  /^ResizeObserver loop limit exceeded$/,
];
const FIRST_URL = /https?:\/\/[^\s)]+/;

export function isIgnoredBrowserError(event: ErrorEvent | PromiseRejectionEvent): boolean {
  try {
    const { message, origin } = describeBrowserError(event);
    if (RESIZE_OBSERVER_LOOP.some((re) => re.test(message))) return true;
    return TELNYX_TORN_DOWN_PEER.some((re) => re.test(message)) && TELNYX_CHUNK.test(origin);
  } catch {
    return false;
  }
}

/**
 * `message` is the thrown Error's message, else the event's own (a
 * ResizeObserver notice has no Error), else the rejection reason when it is a
 * plain string. `origin` is where it was thrown: the event's `filename`, else
 * the first frame of the stack that names a URL.
 */
function describeBrowserError(event: ErrorEvent | PromiseRejectionEvent): {
  message: string;
  origin: string;
} {
  const thrown: unknown = 'reason' in event ? event.reason : event.error;
  const error = thrown instanceof Error ? thrown : null;
  const eventMessage = 'message' in event && typeof event.message === 'string' ? event.message : '';
  const filename = 'filename' in event && typeof event.filename === 'string' ? event.filename : '';
  const firstFrame = error?.stack?.match(FIRST_URL)?.[0] ?? '';
  return {
    message: error?.message || eventMessage || (typeof thrown === 'string' ? thrown : ''),
    origin: filename || firstFrame,
  };
}

/** Build the `aws-rum-web` config for a resolved runtime config. */
export function buildRumConfig(config: RumRuntimeConfig): AwsRumConfig {
  const apiUrls = apiUrlMatchers();
  return {
    sessionSampleRate: config.sessionSampleRate,
    identityPoolId: config.identityPoolId,
    guestRoleArn: config.guestRoleArn,
    endpoint: `https://dataplane.rum.${config.region}.amazonaws.com`,
    telemetries: [
      // Third-party noise never reaches the JS-error metric (see the filter).
      ['errors', { ignore: isIgnoredBrowserError }],
      'performance',
      [
        'http',
        {
          // Only our API: no third-party URLs in events, no foreign preflights.
          urlsToInclude: apiUrls,
          // Leave header ownership to the API clients in correlation-only mode.
          addXRayTraceIdHeader: RUM_TRACING_ENABLED ? apiUrls : false,
          // Failed requests only; successful ones remain in backend access logs.
          recordAllRequests: false,
        },
      ],
    ],
    allowCookies: true,
    enableXRay: RUM_TRACING_ENABLED,
    // W3C `traceparent` rather than `X-Amzn-Trace-Id`, matching the backend
    // propagator order (W3CTraceContext first).
    enableW3CTraceId: RUM_TRACING_ENABLED,
    // Page views come from the router with the ROUTE TEMPLATE as the page id
    // (`/contacts/$contactId`), not the raw pathname — see
    // `installRouterPageViews`. The built-in history patch would record every
    // distinct id as its own page.
    disableAutoPageView: true,
    // Resource timing entries stay, their URLs do not: signed asset URLs and
    // file names can carry customer data, which no RUM attribute may.
    recordResourceUrl: false,
    sessionAttributes: currentSessionAttributes(),
  };
}

export async function initMonitoring() {
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
  if (sentryDsn) {
    const Sentry = await import('@sentry/react');
    _sentry = Sentry;
    Sentry.init({
      ...sentryInitOptions(sentryDsn),
      integrations: [
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
    });
  }

  const rumConfig = readRumConfig();
  if (rumConfig) {
    try {
      const { AwsRum } = await import('aws-rum-web');
      const rum = new AwsRum(
        rumConfig.appMonitorId,
        applicationVersion(),
        rumConfig.region,
        buildRumConfig(rumConfig),
      );
      _rum = rum;
      // Only hand over header ownership when RUM actually injects it.
      setTraceparentOwnedExternally(RUM_TRACING_ENABLED);
      _lastSessionAttributes = JSON.stringify(currentSessionAttributes());
      subscribeSessionAttributes();
      if (_pendingPage) recordPage(_pendingPage);
    } catch (error) {
      // RUM is best-effort: a client that fails to construct (blocked
      // dataplane, broken identity pool) must never take the app down with it.
      _rum = null;
      setTraceparentOwnedExternally(false);
      _sentry?.captureException(error, { tags: { source: 'rum-init' } });
      if (import.meta.env.DEV) console.warn('[monitoring] CloudWatch RUM failed to start', error);
    }
  }
}

/**
 * True once RUM is injecting `traceparent` on API requests itself. The axios
 * clients mint their own only while this is false (they read the same flag
 * from `trace-context.ts`).
 */
export function isRumPropagatingTraceContext(): boolean {
  return isTraceparentOwnedExternally();
}

// ─── Session attributes ───────────────────────────────────────────────────────

type SessionAttributes = { tenantId: string; userId: string; role: string };

function currentSessionAttributes(): SessionAttributes {
  const auth = useAuthStore.getState();
  return {
    tenantId: getActiveTenantId() ?? '',
    userId: auth.userId ?? '',
    role: [...auth.roles].sort().join(','),
  };
}

function pushSessionAttributes() {
  if (!_rum) return;
  const next = currentSessionAttributes();
  const key = JSON.stringify(next);
  if (key === _lastSessionAttributes) return;
  _lastSessionAttributes = key;
  _rum.addSessionAttributes(next);
}

/**
 * Keep RUM's session attributes in step with login / logout / tenant switch.
 * Identifiers only (tenant id, user id, role names) — never email or name.
 */
function subscribeSessionAttributes() {
  useAuthStore.subscribe(pushSessionAttributes);
  useTenantStore.subscribe(pushSessionAttributes);
}

// ─── Page views ───────────────────────────────────────────────────────────────

/**
 * The slice of a TanStack Router this module needs. Structural so tests can
 * pass a stub and so `monitoring.ts` never imports the app's route tree.
 */
export interface PageViewRouter {
  subscribe(
    eventType: 'onResolved',
    fn: (event: { toLocation: { pathname: string }; pathChanged: boolean }) => void,
  ): () => void;
  state: { matches: ReadonlyArray<{ routeId: string; fullPath: string }> };
}

function recordPage(page: { pageId: string; routeId: string }) {
  _rum?.recordPageView({ pageId: page.pageId, pageAttributes: { routeId: page.routeId } });
}

/**
 * Record a RUM page view per resolved navigation, keyed by the matched ROUTE
 * TEMPLATE (`/contacts/$contactId`) so a page id is one row per screen, not
 * one per record. Fires for the initial load too — with `disableAutoPageView`
 * the landing page is ours to record. Safe before RUM is up: the latest page
 * is parked and recorded as soon as the client initializes.
 */
export function installRouterPageViews(router: PageViewRouter): () => void {
  return router.subscribe('onResolved', () => {
    const leaf = router.state.matches[router.state.matches.length - 1];
    if (!leaf) return;
    const page = { pageId: leaf.fullPath, routeId: leaf.routeId };
    if (_pendingPage?.pageId === page.pageId) return;
    _pendingPage = page;
    recordPage(page);
  });
}

// ─── Errors ───────────────────────────────────────────────────────────────────

/**
 * Forward a React Query cache error to Sentry (with query context) and to
 * CloudWatch RUM. No-op for whichever client is not initialized.
 *
 * RUM gets a `notifi.query_error` custom event for every failure, carrying the
 * request id so the event can be joined to the backend log line. It also gets
 * a `recordError` — but only for failures that are not a 4xx verdict: a 403 on
 * a read or a 404 is the server answering, already recorded by the HTTP
 * telemetry as a failed request, and counting it as a JavaScript error would
 * inflate the "JS error rate" alarm with traffic that broke nothing.
 */
export function captureQueryError(error: unknown, queryKey: readonly unknown[]) {
  const apiError = isApiError(error) ? error : null;
  const requestId = apiError?.requestId;

  _sentry?.captureException(error, {
    tags: { source: 'react-query' },
    extra: { queryKey, requestId },
  });

  if (!_rum) return;
  _rum.recordEvent('notifi.query_error', {
    queryKey: String(queryKey[0] ?? ''),
    status: apiError?.status ?? 0,
    code: apiError?.code ?? 'UNKNOWN',
    message: error instanceof Error ? error.message : String(error),
    requestId: requestId ?? '',
    traceId: apiError?.traceId ?? '',
  });
  const isClientVerdict = apiError !== null && apiError.status >= 400 && apiError.status < 500;
  if (!isClientVerdict) _rum.recordError(error);
}

// ─── Custom actions ───────────────────────────────────────────────────────────

/**
 * Track a key business action as a CloudWatch RUM custom event
 * (`notifi.action` with `{ name, ...context }`), so it can be counted and
 * funnelled in RUM dashboards. No-op when RUM is not initialized.
 *
 * `context` must be PII-free: record ids, enum-like strings, amounts and
 * counts are fine; never a name, email, phone number, address, message body
 * or free text. RUM events are retained and searchable — treat every value as
 * something that will show up in a dashboard.
 *
 * @example
 * trackAction('booking_created', { jobType: 'plumbing', source: 'online-form' });
 * trackAction('invoice_sent', { invoiceId: id, channel });
 * trackAction('quote_accepted', { quoteId: id });
 */
export function trackAction(name: string, context?: Record<string, unknown>) {
  _rum?.recordEvent('notifi.action', { name, ...context });
}
